import { ChangeEvent, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { ROLES, Role } from "@equipped/core/auth/roles";
import { AdminUserLookupResult, GrantSpec } from "@equipped/core/backend/client";
import { useAuth } from "../auth/AuthContext";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { GrantPicker } from "../components/GrantPicker";
import { MainCard } from "../components/MainCard";
import { publicContentBaseUrl } from "../lib/contentBase";
import { useCatalogTree } from "../lib/useCatalogTree";

const LOOKUP_BATCH_SIZE = 500;
const GRANT_BATCH_SIZE = 500;
const PER_USER_CONCURRENCY = 4;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ParsedEmailList {
  emails: string[];
  sourceCount: number;
  duplicateCount: number;
  invalidCount: number;
  headerSkipped: boolean;
}

type BulkActionKind = "grant" | "role" | "suspend" | "reinstate";
type RowRunState = "pending" | "running" | "success" | "failed";

interface RowActionResult {
  action: BulkActionKind;
  state: RowRunState;
  message: string;
}

interface BulkActionRequest {
  kind: BulkActionKind;
  userIds: string[];
  role?: Role;
  reason?: string;
  grants?: GrantSpec[];
}

interface RunProgress {
  label: string;
  total: number;
  completed: number;
  succeeded: number;
  failed: number;
  running: boolean;
}

interface RunSummary {
  label: string;
  total: number;
  succeeded: number;
  failed: number;
}

function chunks<T>(items: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let index = 0; index < items.length; index += size) result.push(items.slice(index, index + size));
  return result;
}

async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  operation: (item: T) => Promise<void>,
): Promise<void> {
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < items.length) {
      const item = items[nextIndex];
      nextIndex += 1;
      await operation(item);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

/** Reads the first field of every CSV record, including quoted fields and escaped quotes. */
function firstCsvColumn(text: string): string[] {
  const values: string[] = [];
  let firstField = "";
  let inQuotes = false;
  let readingFirstField = true;

  const finishRecord = () => {
    values.push(firstField);
    firstField = "";
    readingFirstField = true;
  };

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (character === '"') {
      if (inQuotes && text[index + 1] === '"') {
        if (readingFirstField) firstField += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (character === "," && !inQuotes) {
      readingFirstField = false;
    } else if ((character === "\n" || character === "\r") && !inQuotes) {
      if (character === "\r" && text[index + 1] === "\n") index += 1;
      finishRecord();
    } else if (readingFirstField) {
      firstField += character;
    }
  }
  if (firstField || !readingFirstField) finishRecord();
  return values;
}

function parseEmailCandidates(candidates: string[], fromCsv: boolean): ParsedEmailList {
  const normalized = candidates.map((value) => value.replace(/^\uFEFF/, "").trim().toLowerCase()).filter(Boolean);
  const headerSkipped = fromCsv && normalized[0] === "email";
  const values = headerSkipped ? normalized.slice(1) : normalized;
  const emails: string[] = [];
  const seen = new Set<string>();
  let duplicateCount = 0;
  let invalidCount = 0;

  for (const value of values) {
    if (!EMAIL_PATTERN.test(value)) {
      invalidCount += 1;
    } else if (seen.has(value)) {
      duplicateCount += 1;
    } else {
      seen.add(value);
      emails.push(value);
    }
  }

  return { emails, sourceCount: values.length, duplicateCount, invalidCount, headerSkipped };
}

function actionLabel(kind: BulkActionKind): string {
  if (kind === "grant") return "Grant catalogs";
  if (kind === "role") return "Set role";
  if (kind === "suspend") return "Suspend";
  return "Reinstate";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function accountState(result: AdminUserLookupResult): string {
  if (!result.exists) return "No account";
  if (result.provisionedAt && !result.claimedAt) return "Provisioned, not claimed";
  if (result.claimedAt) return "Claimed";
  return "Account exists";
}

function dateLabel(epochSeconds: number | null | undefined): string {
  return epochSeconds ? new Date(epochSeconds * 1000).toLocaleDateString() : "—";
}

/**
 * Dedicated bulk account operations (docs/384 idea #11). This is deliberately a
 * frontend orchestration layer over the existing lookup, grant, role, and moderation
 * client methods: lookup/grant calls are chunked to their existing 500-row caps and
 * per-user writes run with bounded concurrency so a large list cannot flood the Worker.
 */
export function BulkOperationsPage() {
  const { client, token, backendUrl } = useAuth();
  const contentBaseUrl = publicContentBaseUrl(backendUrl);
  const queryClient = useQueryClient();
  const { tree, error: treeError } = useCatalogTree(backendUrl);
  const [rawEmails, setRawEmails] = useState("");
  const [csvEmails, setCsvEmails] = useState<string[] | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [grants, setGrants] = useState<GrantSpec[]>([]);
  const [grantEditorOpen, setGrantEditorOpen] = useState(false);
  const [role, setRole] = useState<Role>("viewer");
  const [confirmAction, setConfirmAction] = useState<BulkActionRequest | null>(null);
  const [rowResults, setRowResults] = useState<Map<string, RowActionResult>>(new Map());
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const [summary, setSummary] = useState<RunSummary | null>(null);

  const parsed = useMemo(
    () => parseEmailCandidates(csvEmails ?? rawEmails.split(/[\n,]/), csvEmails !== null),
    [csvEmails, rawEmails],
  );

  const clearLookup = () => {
    lookupMutation.reset();
    setSelectedUserIds(new Set());
    setRowResults(new Map());
    setProgress(null);
    setSummary(null);
  };

  const lookupMutation = useMutation({
    mutationFn: async (emails: string[]) => {
      const batches = chunks(emails, LOOKUP_BATCH_SIZE);
      const resultBatches: AdminUserLookupResult[][] = Array.from({ length: batches.length });
      await runWithConcurrency(batches.map((batch, index) => ({ batch, index })), PER_USER_CONCURRENCY, async ({ batch, index }) => {
        resultBatches[index] = await client!.adminUsersLookup(token!, batch);
      });
      const byEmail = new Map(resultBatches.flat().map((result) => [result.email, result]));
      if (emails.some((email) => !byEmail.has(email))) {
        throw new Error("The lookup returned an incomplete result. Try again before running an action.");
      }
      return emails.map((email) => byEmail.get(email)!);
    },
    onSuccess: (results) => {
      setSelectedUserIds(new Set(results.filter((result) => result.exists && result.userId).map((result) => result.userId!)));
      setRowResults(new Map());
      setProgress(null);
      setSummary(null);
    },
  });

  const updateRowResult = (userId: string, result: RowActionResult) => {
    setRowResults((current) => {
      const next = new Map(current);
      next.set(userId, result);
      return next;
    });
  };

  const operationMutation = useMutation({
    mutationFn: async (request: BulkActionRequest): Promise<RunSummary> => {
      const label = actionLabel(request.kind);
      let succeeded = 0;
      let failed = 0;
      let completed = 0;
      setSummary(null);
      setRowResults(
        new Map(request.userIds.map((userId) => [userId, { action: request.kind, state: "pending", message: "Queued" }])),
      );
      setProgress({ label, total: request.userIds.length, completed: 0, succeeded: 0, failed: 0, running: true });

      const finishRow = (userId: string, success: boolean, message: string) => {
        completed += 1;
        if (success) succeeded += 1;
        else failed += 1;
        updateRowResult(userId, { action: request.kind, state: success ? "success" : "failed", message });
        setProgress({ label, total: request.userIds.length, completed, succeeded, failed, running: true });
      };

      if (request.kind === "grant") {
        for (const batch of chunks(request.userIds, GRANT_BATCH_SIZE)) {
          batch.forEach((userId) => updateRowResult(userId, { action: request.kind, state: "running", message: "Applying…" }));
          try {
            const outcomes = await client!.adminGrantBulk(token!, batch, request.grants ?? []);
            const byUserId = new Map(outcomes.map((outcome) => [outcome.userId, outcome]));
            for (const userId of batch) {
              const outcome = byUserId.get(userId);
              if (!outcome || outcome.outcome === "skipped") {
                finishRow(userId, false, outcome?.reason ?? "No result returned");
              } else if (outcome.outcome === "already-had") {
                finishRow(userId, true, "Already had every selected grant");
              } else {
                finishRow(userId, true, `${outcome.granted.length} grant(s) added`);
              }
            }
          } catch (error) {
            batch.forEach((userId) => finishRow(userId, false, errorMessage(error)));
          }
        }
      } else {
        await runWithConcurrency(request.userIds, PER_USER_CONCURRENCY, async (userId) => {
          updateRowResult(userId, { action: request.kind, state: "running", message: "In progress…" });
          try {
            if (request.kind === "role") {
              await client!.adminSetRole(token!, userId, request.role!);
            } else if (request.kind === "suspend") {
              await client!.adminSuspend(token!, { userId, reason: request.reason || undefined });
            } else {
              await client!.adminReinstate(token!, userId);
            }
            finishRow(userId, true, request.kind === "role" ? `Role set to ${request.role}` : `${label} succeeded`);
          } catch (error) {
            finishRow(userId, false, errorMessage(error));
          }
        });
      }

      setProgress({ label, total: request.userIds.length, completed, succeeded, failed, running: false });
      return { label, total: request.userIds.length, succeeded, failed };
    },
    onSuccess: (result, request) => {
      setSummary(result);
      if (request.kind === "role") void queryClient.invalidateQueries({ queryKey: ["admin-users-page"] });
      if (request.kind === "grant") void queryClient.invalidateQueries({ queryKey: ["admin-grants"] });
      if (request.kind === "suspend" || request.kind === "reinstate") {
        void queryClient.invalidateQueries({ queryKey: ["admin-moderation"] });
      }
    },
  });

  const results = lookupMutation.data ?? null;
  const matched = useMemo(() => (results ?? []).filter((result) => result.exists && result.userId), [results]);
  const selectedCount = selectedUserIds.size;
  const actionsDisabled = selectedCount === 0 || operationMutation.isPending;

  const replaceInput = (nextRaw: string, nextCsv: string[] | null, nextFileName: string | null) => {
    setRawEmails(nextRaw);
    setCsvEmails(nextCsv);
    setFileName(nextFileName);
    setFileError(null);
    clearLookup();
  };

  const importCsv = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setFileError("Choose a .csv file.");
      return;
    }
    try {
      const values = firstCsvColumn(await file.text());
      replaceInput("", values, file.name);
    } catch (error) {
      setFileError(`Couldn't read ${file.name}: ${errorMessage(error)}`);
    }
  };

  const toggleSelected = (userId: string) => {
    setSelectedUserIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAllMatched = () => {
    setSelectedUserIds(
      selectedUserIds.size === matched.length ? new Set() : new Set(matched.map((result) => result.userId!)),
    );
  };

  const toggleGrant = (kind: GrantSpec["kind"], ref: string) => {
    setGrants((current) =>
      current.some((grant) => grant.kind === kind && grant.ref === ref)
        ? current.filter((grant) => !(grant.kind === kind && grant.ref === ref))
        : [...current, { kind, ref }],
    );
  };

  const startConfirmation = (kind: BulkActionKind, extras: Partial<BulkActionRequest> = {}) => {
    setConfirmAction({ kind, userIds: [...selectedUserIds], ...extras });
  };

  const confirmationBody = confirmAction
    ? confirmAction.kind === "grant"
      ? `Add ${confirmAction.grants?.length ?? 0} catalog/pack grant(s) to ${confirmAction.userIds.length} account(s)? This is silent: users will not be notified.`
      : confirmAction.kind === "role"
        ? `Set ${confirmAction.userIds.length} account(s) to the ${confirmAction.role} role? This can change administrative privileges.`
        : confirmAction.kind === "suspend"
          ? `Suspend ${confirmAction.userIds.length} account(s)? Suspended users become read-only until reinstated.`
          : `Reinstate ${confirmAction.userIds.length} account(s) and return their moderation status to active?`
    : "";

  return (
    <Stack spacing={3}>
      <MainCard title="Bulk operations" subheader="Import contacts, find existing accounts, then apply an action to selected matches.">
        <Stack spacing={2}>
          <TextField
            label="Email list"
            multiline
            minRows={7}
            fullWidth
            placeholder={"ana@church.org\nbob@church.org\ncarla@church.org"}
            value={csvEmails === null ? rawEmails : csvEmails.join("\n")}
            onChange={(event) => replaceInput(event.target.value, null, null)}
            disabled={lookupMutation.isPending || operationMutation.isPending}
            helperText="Paste one email per line or separate emails with commas. CSV imports use the first column."
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" } }}>
            <Button component="label" variant="outlined" startIcon={<UploadFileOutlinedIcon />} disabled={operationMutation.isPending}>
              Upload .csv
              <input hidden type="file" accept=".csv,text/csv" onChange={importCsv} />
            </Button>
            {fileName ? <Chip label={fileName} size="small" variant="outlined" /> : null}
            <Typography variant="body2" color="text.secondary">
              {parsed.emails.length} unique email(s)
              {parsed.duplicateCount ? ` · ${parsed.duplicateCount} duplicate(s) removed` : ""}
              {parsed.invalidCount ? ` · ${parsed.invalidCount} invalid value(s) ignored` : ""}
              {parsed.headerSkipped ? " · header skipped" : ""}
              {parsed.sourceCount ? ` · ${parsed.sourceCount} imported value(s)` : ""}
            </Typography>
          </Stack>

          {fileError ? <Alert severity="error">{fileError}</Alert> : null}
          {lookupMutation.isError ? <Alert severity="error">{errorMessage(lookupMutation.error)}</Alert> : null}

          <Box>
            <Button
              variant="contained"
              disabled={parsed.emails.length === 0 || lookupMutation.isPending || operationMutation.isPending}
              onClick={() => lookupMutation.mutate(parsed.emails)}
            >
              {lookupMutation.isPending ? "Looking up…" : `Look up ${parsed.emails.length || ""} email(s)`}
            </Button>
          </Box>
        </Stack>
      </MainCard>

      {results ? (
        <MainCard
          title="Lookup results"
          subheader={`${matched.length} of ${results.length} ${results.length === 1 ? "email has" : "emails have"} accounts`}
        >
          <Stack spacing={2.5}>
            {matched.length > 0 ? (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "action.hover",
                  p: 2,
                }}
              >
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" }, flexWrap: "wrap" }}>
                    <Typography variant="subtitle2" sx={{ mr: { sm: "auto" } }}>
                      {selectedCount} matched account(s) selected
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel id="bulk-role-label">Role</InputLabel>
                      <Select labelId="bulk-role-label" label="Role" value={role} onChange={(event) => setRole(event.target.value as Role)}>
                        {ROLES.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <Button variant="outlined" disabled={actionsDisabled} onClick={() => startConfirmation("role", { role })}>
                      Set role
                    </Button>
                    <Button color="warning" variant="outlined" disabled={actionsDisabled} onClick={() => startConfirmation("suspend")}>
                      Suspend
                    </Button>
                    <Button color="success" variant="outlined" disabled={actionsDisabled} onClick={() => startConfirmation("reinstate")}>
                      Reinstate
                    </Button>
                    <Button variant="contained" disabled={actionsDisabled} onClick={() => setGrantEditorOpen((open) => !open)}>
                      Grant catalogs
                    </Button>
                  </Stack>

                  {grantEditorOpen ? (
                    <Box sx={{ bgcolor: "background.paper", borderRadius: 1.5, p: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select catalogs or packs to add. Grants are idempotent and no notification is sent.
                      </Typography>
                      <GrantPicker
                        tree={tree}
                        baseUrl={contentBaseUrl}
                        selected={grants}
                        onToggle={toggleGrant}
                        error={treeError}
                      />
                      <Button
                        variant="contained"
                        disabled={actionsDisabled || grants.length === 0}
                        onClick={() => startConfirmation("grant", { grants: [...grants] })}
                      >
                        Review grant action
                      </Button>
                    </Box>
                  ) : null}
                </Stack>
              </Box>
            ) : (
              <Alert severity="info">None of the imported emails has an account, so there are no bulk actions to run.</Alert>
            )}

            {progress ? (
              <Box>
                <Stack direction="row" sx={{ justifyContent: "space-between", mb: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{progress.label}</Typography>
                  <Typography variant="body2" color="text.secondary">{progress.completed} of {progress.total}</Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress.total ? (progress.completed / progress.total) * 100 : 0}
                  aria-label={`${progress.label} progress`}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            ) : null}

            {summary ? (
              <Alert severity={summary.failed ? "warning" : "success"}>
                {summary.label} finished: {summary.succeeded} succeeded, {summary.failed} failed, {summary.total} total.
              </Alert>
            ) : null}
            {operationMutation.isError ? <Alert severity="error">{errorMessage(operationMutation.error)}</Alert> : null}

            <TableContainer>
              <Table size="small" aria-label="Bulk account lookup results">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={matched.length > 0 && selectedUserIds.size === matched.length}
                        indeterminate={selectedUserIds.size > 0 && selectedUserIds.size < matched.length}
                        disabled={matched.length === 0 || operationMutation.isPending}
                        onChange={toggleAllMatched}
                        slotProps={{ input: { "aria-label": "Select all matched accounts" } }}
                      />
                    </TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Exists?</TableCell>
                    <TableCell>User ID</TableCell>
                    <TableCell>Claimed / provisioned</TableCell>
                    <TableCell align="right">Grants</TableCell>
                    <TableCell>Latest action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result) => {
                    const userId = result.userId;
                    const rowResult = userId ? rowResults.get(userId) : undefined;
                    return (
                      <TableRow key={result.email} hover selected={!!userId && selectedUserIds.has(userId)}>
                        <TableCell padding="checkbox">
                          {result.exists && userId ? (
                            <Checkbox
                              checked={selectedUserIds.has(userId)}
                              disabled={operationMutation.isPending}
                              onChange={() => toggleSelected(userId)}
                              slotProps={{ input: { "aria-label": `Select ${result.email}` } }}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ overflowWrap: "anywhere" }}>{result.email}</TableCell>
                        <TableCell><Chip size="small" color={result.exists ? "success" : "default"} label={result.exists ? "Yes" : "No"} /></TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12, overflowWrap: "anywhere" }}>{userId ?? "—"}</TableCell>
                        <TableCell>
                          {result.exists ? (
                            <Box>
                              <Typography variant="body2">{accountState(result)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Provisioned {dateLabel(result.provisionedAt)} · Claimed {dateLabel(result.claimedAt)}
                              </Typography>
                            </Box>
                          ) : "—"}
                        </TableCell>
                        <TableCell align="right">{result.exists ? (result.grantCount ?? 0) : "—"}</TableCell>
                        <TableCell sx={{ minWidth: 170 }}>
                          {rowResult ? (
                            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                              <Chip
                                size="small"
                                label={rowResult.state}
                                color={rowResult.state === "success" ? "success" : rowResult.state === "failed" ? "error" : "info"}
                                variant={rowResult.state === "pending" ? "outlined" : "filled"}
                              />
                              <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>{rowResult.message}</Typography>
                            </Stack>
                          ) : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </MainCard>
      ) : null}

      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction ? `${actionLabel(confirmAction.kind)} selected accounts?` : "Confirm bulk action"}
        body={confirmationBody}
        confirmLabel={confirmAction ? actionLabel(confirmAction.kind) : "Confirm"}
        busy={operationMutation.isPending}
        error={operationMutation.isError ? errorMessage(operationMutation.error) : null}
        danger={confirmAction?.kind === "suspend" || confirmAction?.kind === "role"}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => {
          if (!confirmAction) return;
          const request = confirmAction;
          setConfirmAction(null);
          if (request.kind === "grant") setGrantEditorOpen(false);
          operationMutation.mutate(request);
        }}
      >
        {confirmAction?.kind === "suspend" ? (
          <TextField
            fullWidth
            size="small"
            label="Reason (optional)"
            value={confirmAction.reason ?? ""}
            onChange={(event) => setConfirmAction({ ...confirmAction, reason: event.target.value })}
            sx={{ mt: 2 }}
          />
        ) : null}
      </ConfirmDialog>
    </Stack>
  );
}
